import classNames from "classnames";
import * as _ from "lodash";
import type { ComponentType, ReactNode } from "react";
import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import actions from "@/actions";
import type { MapMode } from "@/types";

import Tools from "./tools";
import ToolsBarContext from "./ToolsBarContext";

interface Tool {
    name: string;
    button: ComponentType<Record<string, unknown>>;
    panel?: ComponentType;
    panelClass?: string;
}

interface ArrayConfiguration {
    lights: string;
    direction: "right" | "down" | "left" | "up";
    spacing: string;
}

/**
 * Determines whether the redo action is disabled based on mode history and paste state.
 *
 * @param map - The Redux map state containing mode history.
 * @returns Whether the redo action is disabled.
 */
const isRedoDisabled = (map) =>
    map.general.mode.name === "view"
        ? map.values.global.future.length === 0
        : map.values.mode.future.length === 0 ||
          map.general.pasteMode.activated;

/**
 * Determines whether the undo action is disabled based on mode history and paste state.
 *
 * @param map - The Redux map state containing mode history.
 * @returns Whether the undo action is disabled.
 */
const isUndoDisabled = (map) =>
    map.general.mode.name === "view"
        ? map.values.global.past.length === 0
        : map.values.mode.past.length === 0 || map.general.pasteMode.activated;

/**
 * Props for the ToolsBar component.
 */
interface ToolsBarProps {
    /**
     * The current screen orientation, driving the tools bar layout.
     */
    orientation: "portrait" | "landscape";
    /**
     * The current map mode, selecting which tools are available.
     */
    mapMode: MapMode;
    /**
     * Whether the top bar is currently expanded.
     */
    topBarVisible: boolean;
    /**
     * The currently selected tool.
     */
    selectedTool: Tool | null;
    /**
     * Callback to set the selected tool.
     */
    onSelectTool: (tool: Tool | null) => void;
    /**
     * The status and edit panels rendered inside the tools bar.
     */
    children: ReactNode;
    /**
     * Whether the undo action is unavailable.
     */
    undoDisabled: boolean;
    /**
     * Whether the redo action is unavailable.
     */
    redoDisabled: boolean;
    /**
     * Callback to redo the last global change.
     */
    onGlobalRedo: () => void;
    /**
     * Callback to redo the last mode-scoped change.
     */
    onModeRedo: () => void;
    /**
     * Callback to undo the last global change.
     */
    onGlobalUndo: () => void;
    /**
     * Callback to undo the last mode-scoped change.
     */
    onModeUndo: () => void;
    /**
     * The current array-of-lights configuration.
     */
    arrayConfiguration: ArrayConfiguration;
    /**
     * Callback to apply an array-of-lights configuration.
     */
    onConfigureArray: (configuration: ArrayConfiguration) => void;
    /**
     * Callback to clear the array-of-lights configuration.
     */
    onClearConfiguration: () => void;
    /**
     * Callback to set the mode-scoped light selection.
     */
    setSelectLights: (lights: unknown[], mode: string) => void;
    /**
     * The mode-scoped selected light IDs.
     */
    selectedLights: string[];
    /**
     * Callback to clear the current selection.
     */
    deselectAllLights: () => void;
}

const ToolsBarComponent = ({
    orientation,
    mapMode,
    topBarVisible: topBarVisibleProp,
    selectedTool,
    onSelectTool,
    children,
    undoDisabled,
    redoDisabled,
    onGlobalRedo,
    onModeRedo,
    onGlobalUndo,
    onModeUndo,
    arrayConfiguration,
    onConfigureArray,
    onClearConfiguration,
    setSelectLights,
    selectedLights,
    deselectAllLights,
}: ToolsBarProps): ReactNode => {
    const tools = useMemo<Tool[] | typeof Tools.arrayPanel>(() => {
        switch (mapMode.name) {
            case "add":
                return mapMode.props.markerType ? [] : Tools.arrayPanel;
            case "highlight":
            case "firmware":
            case "group":
            case "group-edit":
                return [];
            case "select":
                return [
                    Tools.move,
                    Tools.align,
                    Tools.rotate,
                    Tools.paste,
                    Tools.copy,
                    Tools.delete,
                    Tools.subtract,
                    Tools.undo,
                    Tools.redo,
                    Tools.more,
                ];
            default:
                return [
                    Tools.menu,
                    Tools.add,
                    Tools.group,
                    Tools.select,
                    Tools.rotate,
                    Tools.delete,
                    Tools.search,
                    Tools.filter,
                    Tools.printMap,
                    Tools.highlight,
                    Tools.undo,
                    Tools.redo,
                    Tools.more,
                ];
        }
    }, [mapMode.name, mapMode.props]);

    const onSelectToolHandler = useCallback(
        (toolName: string) => {
            const isSelected = selectedTool && selectedTool.name === toolName;
            const newSelectedTool =
                !isSelected && Array.isArray(tools)
                    ? tools.find((tool) => tool.name === toolName)
                    : null;
            onSelectTool(newSelectedTool);
        },
        [selectedTool, tools, onSelectTool],
    );

    const renderPanel = () => {
        if (Array.isArray(tools)) {
            const panels = tools
                .filter((tool) => tool.panel)
                .map((tool) => {
                    const selected =
                        selectedTool && tool.name === selectedTool.name;
                    const additionalCssClass = tool.panelClass ?? "";
                    return (
                        <div
                            key={`${tool.name}-${orientation}-panel`}
                            className={classNames(
                                "panel display",
                                orientation,
                                {
                                    opened: selected,
                                },
                                additionalCssClass,
                            )}
                        >
                            <tool.panel />
                        </div>
                    );
                });
            return (
                <div className="panel-wrapper">
                    {panels}
                    {children}
                </div>
            );
        }
        const undo = mapMode.name === "view" ? onGlobalUndo : onModeUndo;
        const redo = mapMode.name === "view" ? onGlobalRedo : onModeRedo;
        return (
            <div className="panel-wrapper">
                <div
                    key="unique-panel-portrait"
                    className={`panel ${orientation === "portrait" ? "opened" : ""} portrait`}
                >
                    <Tools.arrayPanel
                        arrayConfiguration={arrayConfiguration}
                        onClearConfiguration={onClearConfiguration}
                        onConfigureArray={onConfigureArray}
                        setSelectLights={setSelectLights}
                        selectedLights={selectedLights}
                        deselectAllLights={deselectAllLights}
                        undo={!undoDisabled ? undo : null}
                        redo={!redoDisabled ? redo : null}
                    />
                </div>
                <div
                    key="unique-panel-landscape"
                    className={`panel ${orientation === "landscape" ? "opened" : ""} landscape`}
                >
                    <Tools.arrayPanel
                        arrayConfiguration={arrayConfiguration}
                        onClearConfiguration={onClearConfiguration}
                        onConfigureArray={onConfigureArray}
                        setSelectLights={setSelectLights}
                        selectedLights={selectedLights}
                        deselectAllLights={deselectAllLights}
                        undo={!undoDisabled ? undo : null}
                        redo={!redoDisabled ? redo : null}
                    />
                </div>
                {children}
            </div>
        );
    };

    const renderButton = (tool: Tool, key: number) => {
        const props = {
            key,
            title: _.capitalize(tool.name),
            selected: selectedTool && tool.name === selectedTool.name,
            onClick: tool.panel ? () => onSelectToolHandler(tool.name) : null,
        };
        return React.createElement(tool.button, props);
    };

    let topBarVisible = topBarVisibleProp;
    if (mapMode.name !== "view") {
        topBarVisible = true;
    }

    return (
        <ToolsBarContext
            value={{
                closePanel: () => onSelectToolHandler(""),
            }}
        >
            <div
                className={`map-container ${orientation} ${topBarVisible ? "top-bar-visible" : ""}`}
            >
                {Array.isArray(tools) && tools.length > 0 && (
                    <div className={`tools-bar-wrapper ${orientation}`}>
                        <div className={`tools-bar ${orientation}`}>
                            {tools.map(renderButton)}
                        </div>
                    </div>
                )}
                {renderPanel()}
            </div>
        </ToolsBarContext>
    );
};

const mapStateToProps = ({ window, map, topBar }) => ({
    orientation: window.orientation,
    mapMode: map.general.mode,
    topBarVisible: topBar.visibility,
    selectedTool: map.general.selectedTool,
    undoDisabled: isUndoDisabled(map),
    redoDisabled: isRedoDisabled(map),
    arrayConfiguration: map.general.arrayOfLights,
    selectedLights: map.values.mode.present.selectedLights,
});

const mapDispatchToProps = {
    onGlobalRedo: actions.map.values.global.redo,
    onModeRedo: actions.map.values.mode.redo,
    onGlobalUndo: actions.map.values.global.undo,
    onModeUndo: actions.map.values.mode.undo,
    onSelectTool: actions.map.general.selectTool,
    onConfigureArray: actions.map.general.configureArrayOfLights,
    onClearConfiguration: actions.map.general.clearArrayOfLights,
    setSelectLights: actions.map.values.mode.selectLights,
    deselectAllLights: actions.map.general.deselectAllLights,
};

export const ToolsBar = connect(
    mapStateToProps,
    mapDispatchToProps,
)(ToolsBarComponent);
